import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FinanceType } from './constants';
import { IncentivesProgram } from './legos/carTypes';

dayjs.extend(utc);

// @TODO add proper types
type Car = any;
type Offer = any;
type IncentivesProgramOffer = any;

export enum IncentiveType {
  EMPLOYEE = 'employee',
  STUDENT = 'student',
  DISABILITY = 'disability',
  MILITARY = 'military',
  CONQUEST = 'conquest',
  LOYALTY = 'loyalty',
  OTHER = 'other',
}

export enum GroupAffiliation {
  AFFILIATE_COMPANY = 'Affiliate Company',
  ASSOCIATION_CLUB_MEMBER = 'Association/Club Member',
  CARD_HOLDERS = 'Card Holders',
  CERTIFICATE = 'Certificate',
  DISABILITY = 'Automobility',
  EDUCATIONAL = 'Educational',
  EMPLOYEE = 'Employee/Retiree - OEM/Dealership',
  FIRST_RESPONDERS = 'First Responders',
  FRIENDS_AND_OR_FAMILY = 'Friends and/or Family',
  MILITARY = 'Military',
  RETIREE = 'Retiree',
  STUDENT = 'College/Student',
  TARGETED = 'Targeted',

  NONE = 'No Specific Group Affiliation',
}

export enum PreviousOwnership {
  LOYALTY = 'Loyalty',
  LEASE_LOYALTY = 'Lease Loyalty',
  CONQUEST = 'Conquest',

  NONE = 'No Previous Ownership Requirement',
}

/**
 * Verify program's group affiliation matches the provided value.
 *
 * @param affiliation Required group affiliation
 */
const matchAffiliation = (affiliation: GroupAffiliation) => (
  incentiveOffer: IncentivesProgramOffer,
) => incentiveOffer.program.groupAffiliation === affiliation;

/**
 * Verify program's previous ownerships matches the provided value.
 *
 * @param ownership Required previous ownership
 */
const matchPreviousOwnership = (ownership: PreviousOwnership) => (
  incentiveOffer: IncentivesProgramOffer,
) =>
  incentiveOffer.program.previousOwnership === ownership &&
  incentiveOffer.program.groupAffiliation === GroupAffiliation.NONE;

/**
 * Verify incentive offer matches the provided finance type.
 *
 * @param financeType Required finance type
 */
const matchFinanceType = (financeType: FinanceType | null) => (
  incentiveOffer: IncentivesProgramOffer,
) => (financeType ? incentiveOffer.financeTypes.includes(financeType) : false);

/**
 * Helper object used to match Incentive Type with the given program offer
 */
const typeMatchers = {
  [IncentiveType.EMPLOYEE]: matchAffiliation(GroupAffiliation.EMPLOYEE),
  [IncentiveType.STUDENT]: matchAffiliation(GroupAffiliation.STUDENT),
  [IncentiveType.MILITARY]: matchAffiliation(GroupAffiliation.MILITARY),
  [IncentiveType.DISABILITY]: matchAffiliation(GroupAffiliation.DISABILITY),
  [IncentiveType.LOYALTY]: matchPreviousOwnership(PreviousOwnership.LOYALTY),
  [IncentiveType.CONQUEST]: matchPreviousOwnership(PreviousOwnership.CONQUEST),
};

const getIncentiveType = (
  programOffer: IncentivesProgramOffer,
): IncentiveType => {
  const entry = Object.entries(typeMatchers).find(([, typeMatcher]) => {
    return typeMatcher(programOffer);
  });
  return entry ? (entry[0] as IncentiveType) : IncentiveType.OTHER;
};

/**
 * Extract incentive offers for a fixed set of types (Employee offer, Student offer etc).
 *
 * @param car Selected vehicle
 * @param financeType Selected finance type
 */
export const getProgramOffersWithTypes = (
  car: Car,
  offer: Offer,
  financeType: FinanceType,
) => {
  const programOffersWithTypes = (car.incentiveOffers ?? [])
    // Filter offers for the given finance type
    .filter(matchFinanceType(financeType))
    // Filter out offers with special terms
    .filter(
      (programOffer: any) =>
        (programOffer.from === undefined || programOffer.from === null) &&
        (programOffer.to === undefined || programOffer.to === null),
    )
    // Exclude oem offers as they are already applied
    .filter((programOffer: any) => {
      const oemOffers = car.oemOffers?.[financeType] ?? [];
      return !oemOffers.some(
        (o: any) =>
          o.programs?.find((p: any) => p.id === programOffer.program.id) !==
          undefined,
      );
    })
    // Add types
    .map((programOffer: any) => ({
      programOffer,
      type: getIncentiveType(programOffer),
    }))
    // Hide any disability incentives
    .filter(
      (programOfferWithType: any) =>
        programOfferWithType.type !== IncentiveType.DISABILITY,
    );

  // Get any selected programs other than those available for user,
  // it could include OEM offers or other others selected by desking manager
  const currentPrograms =
    offer.programs?.filter(
      (p: any) =>
        !programOffersWithTypes?.find(
          (o: any) => o.programOffer.program.id === p.id,
        ),
    ) ?? [];

  // Check the program is stackable with current programs
  return programOffersWithTypes.filter((o: any) =>
    currentPrograms.every((p: any) =>
      o.programOffer.stackablePrograms.includes(p.id),
    ),
  );
};

export const isIncentiveProgramExpired = (program: IncentivesProgram) =>
  dayjs.utc(program?.endDate).add(1, 'day') < dayjs.utc();
